import {
  AddressDTO,
  CartCap,
  CartDTO,
  ClientDTO,
  CreateCartRequest,
  MarcaDTO,
  OrderCreationResult,
  OrderDTO,
  OrdersQueryResponse,
  ProductQuantity,
  ProductQuantityFlat,
} from 'service/apicataleg/types/ApiCatalegTypes';
import useRole from 'hooks/useRole';
import RepresentantService, {
  ReqRegisterClient,
  SuccessRegisterResponse,
} from 'service/apicataleg/api/representant/ClientsRepreService';
import OrderRepreService from 'service/apicataleg/api/representant/OrderRepreService';
import { useAppSelector } from 'store/Store';
import { ClientSliceState } from 'store/ClientsSlice';
import OrderClientService from 'service/apicataleg/api/client/OrderClientService';
import ClientService from 'service/apicataleg/api/client/ClientService';

import { useMemo } from 'react';
import CartClientService from 'service/apicataleg/api/client/CartClientService';
import CartRepreService from 'service/apicataleg/api/representant/CartRepreService';
import CartCommonService from 'service/apicataleg/api/common/CartCommonService';

export interface APIClient {
  getClientDetails(): Promise<ClientDTO | null>;
  getMarques(): Promise<Array<MarcaDTO>>;
  getClientAddresses(): Promise<Array<AddressDTO>>;
  addClientAddress(address: AddressDTO): Promise<AddressDTO | null>;
  updateClientAddress(address: AddressDTO): Promise<boolean>;
}

export interface APIOrder {
  getOrder(npe: number): Promise<OrderDTO | null>;
  getOrders(year: number, mont: number, npes: string): Promise<OrdersQueryResponse | null>;
  getOrderFile(npe: number): Promise<Blob>;
}

export type AddProductsRequest = {
  idCart: number;
  products: Array<ProductQuantityFlat>;
  lang: string;
};

export type ReplaceLinesRequest = {
  idCart: number;
  lines: Array<ProductQuantity>;
};

export type UpdateLineParams = {
  id: string;
  qtty: number;
  delivery: string;
};

export type UpdateLinesRequest = {
  lines: Array<UpdateLineParams>;
};

export interface APICart {
  createOrder(idCart: number): Promise<OrderCreationResult>;
  getCart: (idCart: number, lang: string) => Promise<CartDTO | null>;
  removeLine: (idCart: number, linia: string, lang: string) => Promise<CartDTO | null>;
  removeLines: (idCart: number, linies: Array<string>, lang: string) => Promise<CartDTO | null>;
  addProducts: (req: AddProductsRequest) => Promise<CartDTO | null>;
  replace: (req: ReplaceLinesRequest) => Promise<boolean>;
  updateLines: ({ lines }: UpdateLinesRequest) => Promise<boolean>;
  clearCart: (idCart: number) => Promise<CartDTO | null>;
  saveCartNotes: (idCart: number, notes: string) => Promise<boolean>;
  saveCartNotes2: (idCart: number, notes: string) => Promise<boolean>;
  saveCartAdditionalNotes: (idCart: number, notes: string) => Promise<boolean>;
  saveCartNotificationEmail: (idCart: number, email: string) => Promise<boolean>;
  saveDeliveryDate: (idCart: number, strDate: string) => Promise<boolean>;
  saveSendMail: (idCart: number, isSend: boolean) => Promise<boolean>;
  saveDirEntrega: (idCart: number, codi: number) => Promise<boolean>;
  createCart: (request: CreateCartRequest) => Promise<CartCap | null>;
  deleteCart: (id: number) => Promise<boolean>;
  getCarts: () => Promise<Array<CartCap>>;
}

export interface APIAuth {
  doLogin: (username: string, password: string) => void;
}

export interface APIClientRepresentant extends APIClient {
  registerClient(req: ReqRegisterClient): Promise<SuccessRegisterResponse | null>;
  queryClients(query: string): Promise<Array<ClientDTO>>;
  getClientWithCif(cif: string): Promise<Array<ClientDTO>>;
}

export interface APIOrderClient extends APIOrder {}

interface APIOrderRepresentant extends APIOrder {
  getTokenOrderGuest(npe: number): Promise<string>;
}

export interface CatalegAPIRepresentant {
  apiClient: APIClientRepresentant;
  apiOrder: APIOrderRepresentant;
}

export interface CatalegAPIClient {
  apiClient: APIClient;
  apiOrder: APIOrderClient;
}

const buildRepreClientAPI = (idClient: number): APIClientRepresentant => {
  return {
    getClientDetails: () => RepresentantService.getClientById(idClient),
    getMarques: () => RepresentantService.getMarques(),
    getClientAddresses: () => RepresentantService.getClientAddresses(idClient),
    addClientAddress: (address: AddressDTO) =>
      RepresentantService.addClientAddress(idClient, address),
    updateClientAddress: (address: AddressDTO) =>
      RepresentantService.updateClientAddress(idClient, address),
    registerClient: (req: ReqRegisterClient) => RepresentantService.registerClient(req),
    queryClients: (query: string) => RepresentantService.queryClients(query),
    getClientWithCif: (cif: string) => RepresentantService.getClientWithCif(cif),
  };
};

const buildRepreClientOrder = (idClient: number): APIOrderRepresentant => {
  return {
    getOrder: (npe: number) => OrderRepreService.getOrder(npe),
    getOrders: (year: number, month: number, npes: string) =>
      OrderRepreService.getClientOrders({ client: idClient, year, month, npes }),
    getOrderFile: (npe: number) => OrderRepreService.getOrderFile(npe),
    getTokenOrderGuest: (npe: number) => OrderRepreService.getTokenOrderGuest(npe),
  };
};

const buildApiCart = (role: string, client: number): APICart => {
  return {
    createOrder: (idCart: number) =>
      role === 'client'
        ? CartClientService.createOrder(idCart)
        : CartRepreService.createOrder(idCart),
    getCart: (idCart: number, lang: string) => CartCommonService.getCart({ idCart, lang }),
    removeLine: (idCart: number, linia: string, lang: string) =>
      CartCommonService.removeLine({ idCart, linia, lang }),
    removeLines: (idCart: number, linies: Array<string>, lang: string) =>
      CartCommonService.removeLines({ idCart, linies, lang }),
    addProducts: (req: AddProductsRequest) => CartCommonService.addProducts(req),
    replace: (req: ReplaceLinesRequest) => CartCommonService.replace(req),
    updateLines: (req: UpdateLinesRequest) => CartCommonService.updateLines(req),
    clearCart: (idCart: number) => CartCommonService.clearCart({ idCart }),
    saveCartNotes: (idCart: number, notes: string) =>
      CartCommonService.saveCartNotes({ idCart, notes }),
    saveCartNotes2: (idCart: number, notes: string) =>
      CartCommonService.saveCartNotes2({ idCart, notes }),

    saveCartAdditionalNotes: (idCart: number, notes: string) =>
      CartCommonService.saveCartAdditionalNotes({ idCart, notes }),
    saveCartNotificationEmail: (idCart: number, email: string) =>
      CartCommonService.saveCartNotificationEmail({ idCart, email }),
    saveDeliveryDate: (idCart: number, strDate: string) =>
      CartCommonService.saveDeliveryDate({ idCart, strDate }),
    saveSendMail: (idCart: number, isSend: boolean) =>
      CartCommonService.saveSendMail({ idCart, isSend }),
    saveDirEntrega: (idCart: number, codi: number) =>
      CartCommonService.saveDirEntrega({ idCart, codi }),
    createCart: (request: CreateCartRequest) => {
      return role === 'client'
        ? CartClientService.createCart(request)
        : CartRepreService.createCart({ ...request, client });
    },

    deleteCart: (id: number) => CartCommonService.deleteCart(id),
    getCarts: () =>
      role === 'client'
        ? CartClientService.getClientCarts()
        : CartRepreService.getClientCarts(client),
  };
};

const useApiUser = () => {
  const role = useRole();
  const clientState: ClientSliceState = useAppSelector((state) => state.client);
  const codi = clientState.id;
  const apiUser = useMemo(() => {
    const apiCart: APICart = buildApiCart(role, codi);
    if (role === 'client') {
      return {
        role,
        api: {
          client: ClientService,
          order: OrderClientService,
          cart: apiCart,
        },
      };
    }

    return {
      role,
      client: codi,
      api: { client: buildRepreClientAPI(codi), order: buildRepreClientOrder(codi), cart: apiCart },
    };
  }, [codi, role]);

  return apiUser;
};

export default useApiUser;
